import React, { useCallback, useEffect, useState } from 'react';

import { useMatch, Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import Link from '@mui/material/Link';
import { Tooltip, styled } from '@mui/material';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Axios from '../Assets/Axios/Axios';
import Logo from '../Assets/Logo/white.png';
import LogoBlue from '../Assets/Logo/green.png';
import FBIcon from '../Assets/Icons/SocialMedia/facebook.png';
import InstagramIcon from '../Assets/Icons/SocialMedia/insta.png';
import TwitterIcon from '../Assets/Icons/SocialMedia/twitter.png';
import TelegramIcon from '../Assets/Icons/SocialMedia/telegram.png';
import WhatsappIcon from '../Assets/Icons/SocialMedia/whatsapp.png';

import { COLORS } from '../Constant/COLORS';

import { useToastContext } from '../Context/ToastContext';

import { useIsMobile } from '../Hooks/useIsMobile';

import Reveal from './Reveal';
import MobileWrapperFooterDropDown from './MobileWrapperFooterDropDown';

import { CompanyInformationQuery, GetCategories } from '../Server/Query';

import { toCapitalize } from '../Helpers';

import { TypoWhite } from './Typo';

// Company info is the same for everyone and changes rarely, but <Footer> is
// re-mounted on every page (Home, Category, Seller, dashboards, ...). Cache the
// request at module scope so all Footer instances share a single network call
// per app session instead of re-fetching on every navigation.
let companyInformationRequest = null;

const loadCompanyInformation = () => {
  if (!companyInformationRequest) {
    companyInformationRequest = Axios(CompanyInformationQuery).catch(error => {
      companyInformationRequest = null; // allow a retry after a failed request
      throw error;
    });
  }
  return companyInformationRequest;
};

// Same module-scope caching for the footer's category links.
let categoriesRequest = null;
const loadCategories = () => {
  if (!categoriesRequest) {
    categoriesRequest = Axios(GetCategories).catch(error => {
      categoriesRequest = null;
      throw error;
    });
  }
  return categoriesRequest;
};

function Footer() {
  const { showToastMessage } = useToastContext();

  const isRegProfPage = useMatch('/professional');
  const isMobile = useIsMobile();

  const [companyInformation, setCompanyInformation] = useState({
    facebookUrl: null,
    twitterUrl: null,
    whatsappUrl: null,
    telegramUrl: null,
    instagramUrl: null,
  });

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadCategories()
      .then(response => {
        if (!response?.errors && response?.data?.categories) {
          setCategories(response.data.categories);
        }
      })
      .catch(() => undefined);
  }, []);

  const fetchCompanyInformation = useCallback(async () => {
    try {
      const response = await loadCompanyInformation();

      if (response.errors) {
        showToastMessage('Something went wrong, Failed to get company details.');
        return undefined;
      }
      const parsed = parseCompanyInformation(response.data.companyInformation);
      setCompanyInformation(parsed);
    } catch {
      // network failure is already surfaced via the global API error toast
    }
  }, [showToastMessage]);

  useEffect(() => {
    fetchCompanyInformation();
  }, [fetchCompanyInformation]);

  const socials = [
    { url: companyInformation.facebookUrl, src: FBIcon, alt: 'facebook' },
    { url: companyInformation.instagramUrl, src: InstagramIcon, alt: 'instagram' },
    { url: companyInformation.telegramUrl, src: TelegramIcon, alt: 'telegram' },
    { url: companyInformation.twitterUrl, src: TwitterIcon, alt: 'twitter' },
    { url: companyInformation.whatsappUrl, src: WhatsappIcon, alt: 'whatsapp' },
  ].filter(s => s.url !== null);

  // ── Professional-registration variant: full teal footer ──
  if (isRegProfPage) {
    return (
      <Box sx={{ background: COLORS.tealGradient, color: '#fff' }}>
        <Grid
          justifyContent='space-between'
          container
          columns={5}
          mx='auto'
          maxWidth={{ xs: '90%', md: '80%' }}
          alignItems='center'
          pt={{ xs: '2.5em', md: '5em' }}
          sx={{
            '& .MuiListItem-root': { color: '#fff' },
            '& .MuiLink-root': {
              color: 'rgba(255,255,255,0.85)',
              fontWeight: 500,
              transition: 'color .2s ease',
              '&:hover': { color: '#fff' },
            },
          }}
        >
          <Grid
            item
            md={5}
            component={Typography}
            variant='h4'
            color='#fff'
            fontWeight={700}
            mx={{ xs: 'auto', md: 'unset' }}
            mb={{ xs: 3, md: 4 }}
          >
            Sign up and start
            <br />
            your work today!
          </Grid>

          <Grid item xs={5} md={1} mb={{ xs: 2, md: 0 }}>
            <FooterColumnTitle onDark>Support</FooterColumnTitle>
            <List disablePadding>
              {['Help & Support', 'Trust & Safety'].map(label => (
                <Typography
                  key={label}
                  component={ListItem}
                  variant='body2'
                  sx={{ color: 'rgba(255,255,255,0.85)', py: 0.5, px: 0 }}
                >
                  {label}
                </Typography>
              ))}
              <Link component={RouterLink} to='/categories' variant='body2' sx={{ display: 'block', py: 0.5 }}>
                Hire on Evento
              </Link>
              <Link component={RouterLink} to='/professional' variant='body2' sx={{ display: 'block', py: 0.5 }}>
                Sell on Evento
              </Link>
            </List>
          </Grid>

          <Grid item xs={5} md={1} mb={{ xs: 2, md: 0 }}>
            <FooterColumnTitle onDark>About</FooterColumnTitle>
            <List disablePadding>
              {ABOUT_LINKS.map(({ label, to }) => (
                <Link key={label} component={RouterLink} to={to} variant='body2' sx={{ display: 'block', py: 0.5 }}>
                  {label}
                </Link>
              ))}
            </List>
          </Grid>

          <Grid item xs={5} md={1.6}>
            <NewsletterCard glass />
          </Grid>
        </Grid>

        <Box maxWidth='80%' mx='auto' mt={4}>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.18)' }} />
        </Box>
        <BottomBar onDark socials={socials} logo={Logo} />
      </Box>
    );
  }

  // ── Default footer (public pages) ──
  return (
    <Box component='footer' sx={{ backgroundColor: COLORS.surface, mt: 8 }}>
      <Divider />
      <Box maxWidth={{ xs: '90%', md: '80%' }} mx='auto' pt={{ xs: 4, md: 7 }}>
        <Reveal>
          <Grid container columns={12} rowSpacing={4} columnSpacing={{ md: 4 }}>
            {/* Brand + newsletter */}
            <Grid item xs={12} md={5}>
              <img src={LogoBlue} alt='logo' width={172} height={58} style={{ objectFit: 'contain' }} />
              <Typography variant='body2' color={COLORS.inkSoft} mt={2} maxWidth={360}>
                Sri Lanka&rsquo;s marketplace for trusted event professionals —
                photographers, decorators, caterers and more, all in one place.
              </Typography>
              <Box mt={3} maxWidth={420}>
                <NewsletterCard />
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container columns={12} rowSpacing={2}>
                <Grid item xs={6} md={4}>
                  <MobileWrapperFooterDropDown title='Categories'>
                    <FooterColumnTitle desktopOnly={!isMobile}>Categories</FooterColumnTitle>
                    <List disablePadding sx={linkListSx}>
                      {categories.slice(0, 6).map(category => (
                        <Link
                          key={category.id}
                          component={RouterLink}
                          to={`/category/${category.slug}`}
                          variant='body2'
                          sx={{ display: 'block', py: 0.6 }}
                        >
                          {toCapitalize(category.name)}
                        </Link>
                      ))}
                      <Link component={RouterLink} to='/categories' variant='body2' sx={{ display: 'block', py: 0.6, fontWeight: 700 }}>
                        View all categories
                      </Link>
                    </List>
                  </MobileWrapperFooterDropDown>
                </Grid>

                <Grid item xs={6} md={4}>
                  <MobileWrapperFooterDropDown title='Support'>
                    <FooterColumnTitle desktopOnly={!isMobile}>Support</FooterColumnTitle>
                    <List disablePadding sx={linkListSx}>
                      <Typography component={ListItem} variant='body2' sx={{ color: COLORS.inkSoft, py: 0.6, px: 0 }}>
                        Help & Support
                      </Typography>
                      <Typography component={ListItem} variant='body2' sx={{ color: COLORS.inkSoft, py: 0.6, px: 0 }}>
                        Trust & Safety
                      </Typography>
                      <Link component={RouterLink} to='/categories' variant='body2' sx={{ display: 'block', py: 0.6 }}>
                        Hire on Evento
                      </Link>
                      <Link component={RouterLink} to='/professional' variant='body2' sx={{ display: 'block', py: 0.6 }}>
                        Sell on Evento
                      </Link>
                    </List>
                  </MobileWrapperFooterDropDown>
                </Grid>

                <Grid item xs={12} md={4}>
                  <MobileWrapperFooterDropDown title='About'>
                    <FooterColumnTitle desktopOnly={!isMobile}>About</FooterColumnTitle>
                    <List disablePadding sx={linkListSx}>
                      {ABOUT_LINKS.map(({ label, to }) => (
                        <Link key={label} component={RouterLink} to={to} variant='body2' sx={{ display: 'block', py: 0.6 }}>
                          {label}
                        </Link>
                      ))}
                    </List>
                  </MobileWrapperFooterDropDown>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Reveal>

        <Divider sx={{ mt: { xs: 3, md: 6 } }} />
      </Box>

      <BottomBar socials={socials} logo={LogoBlue} />
    </Box>
  );
}

const linkListSx = {
  '& .MuiLink-root': {
    color: COLORS.inkSoft,
    fontWeight: 500,
    transition: 'color .2s ease, padding-left .2s ease',
    '&:hover': { color: COLORS.teal, pl: 0.5 },
  },
};

const ABOUT_LINKS = [
  { label: 'Careers', to: '/careers' },
  { label: 'Press & News', to: '/press' },
  { label: 'Partnerships', to: '/partnerships' },
  { label: 'Privacy Policy', to: '/privacy-policy' },
  { label: 'Terms of Service', to: '/terms-of-service' },
];

const FooterColumnTitle = ({ children, onDark, desktopOnly }) => (
  <Typography
    variant='overline'
    sx={{
      display: desktopOnly ? { xs: 'none', md: 'block' } : 'block',
      color: onDark ? 'rgba(255,255,255,0.7)' : COLORS.inkSoft,
      fontWeight: 700,
      letterSpacing: '1.4px',
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

// Teal gradient newsletter capture card.
const NewsletterCard = ({ glass }) => (
  <Tooltip title='feature coming soon'>
    <Box
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 4,
        background: glass ? 'rgba(255,255,255,0.10)' : COLORS.tealGradient,
        border: glass ? '1px solid rgba(255,255,255,0.18)' : 'none',
        color: '#fff',
      }}
    >
      <TypoWhite variant='h6' fontWeight={700}>
        Save time, save money
      </TypoWhite>
      <TypoWhite variant='body2' sx={{ color: 'rgba(255,255,255,0.82)', mt: 0.5 }}>
        Get the best event deals in your inbox.
      </TypoWhite>
      <Stack direction='row' gap={1} mt={2}>
        <TextField
          placeholder='Your email'
          fullWidth
          sx={{
            bgcolor: '#fff',
            borderRadius: 2,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          }}
        />
        <Button color='white1' disabled sx={{ color: COLORS.teal, flexShrink: 0 }}>
          Subscribe
        </Button>
      </Stack>
    </Box>
  </Tooltip>
);

// Bottom legal bar with © line, quick links and social icons.
const BottomBar = ({ onDark, socials, logo }) => (
  <Box sx={{ py: 2.5 }}>
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      maxWidth={{ xs: '90%', md: '80%' }}
      mx='auto'
      alignItems='center'
      gap={{ xs: 2, md: 3 }}
    >
      <img src={logo} alt='logo' width={140} height={46} style={{ objectFit: 'contain' }} />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems='center'
        gap={{ xs: 1.5, md: 3 }}
        flexWrap='wrap'
        justifyContent={{ md: 'center' }}
        sx={{
          '& .MuiLink-root': {
            color: onDark ? 'rgba(255,255,255,0.8)' : COLORS.inkSoft,
            fontWeight: 500,
            fontSize: '0.82rem',
            transition: 'color .2s ease',
            '&:hover': { color: onDark ? '#fff' : COLORS.teal },
          },
        }}
      >
        {ABOUT_LINKS.map(({ label, to }) => (
          <Link key={label} component={RouterLink} to={to} variant='body2'>
            {label}
          </Link>
        ))}
      </Stack>

      <Stack
        direction='row'
        gap={1}
        alignItems='center'
        ml={{ md: 'auto' }}
      >
        {socials.map(({ url, src, alt }) => (
          <a key={alt} href={url} referrerPolicy='no-referrer' rel='noreferrer' target='_blank'>
            <SocialMediaIcon src={src} alt={alt} />
          </a>
        ))}
      </Stack>
    </Stack>

    <Typography
      textAlign='center'
      variant='caption'
      sx={{ display: 'block', mt: 2, color: onDark ? 'rgba(255,255,255,0.7)' : COLORS.inkFaint }}
    >
      © evento.lk {getYearRange()}. Made with care for our users.
    </Typography>
  </Box>
);

const SocialMediaIcon = styled('img')({
  width: 34,
  height: 34,
  objectFit: 'cover',
  borderRadius: 8,
  transition: 'transform .2s ease',
  '&:hover': { transform: 'translateY(-3px)' },
});

function parseCompanyInformation(companyInformation) {
  // socialMedia is stored as [facebookUrl, instagramUrl, twitterUrl, telegramUrl, whatsappUrl]
  const [facebookUrl = null, instagramUrl = null, twitterUrl = null, telegramUrl = null, whatsappUrl = null] =
    companyInformation?.socialMedia ?? [];
  return { facebookUrl, instagramUrl, twitterUrl, telegramUrl, whatsappUrl };
}

function getYearRange() {
  const fromYear = 2023;
  const currentYear = new Date().getFullYear();

  return fromYear === currentYear ? fromYear : `${fromYear}-${currentYear}`;
}

export default React.memo(Footer);
